// packages/core/src/learning/school-modules/adulting-life-skills.ts
// ─────────────────────────────────────────────────────────────────────────
// LENS PACK — "Adulting Life Skills" (surface: 'school').
//
// Conforms to LearningModule (LearningTypes.ts) and passes _validateLensPacks:
//   - surface: 'school', homeSchool: 'adulting-life-skills', no assessment
//   - every lesson lessonKind: 'school-core', exactly 3 role-tagged flashcards
//   - exactly one applyAction per lesson, all live + allowed on 'lens-lesson'
//     (openDashboard / openSimulator / noToolAction)
//   - lesson ids namespaced `als-*`, flashcard ids `als-*-fc-*` (globally unique)
//   - concepts non-empty, no intra-pack duplicates
//
// Module id / homeSchool match the locked naming convention so the school's
// exhibit nativeModuleId: 'school-adulting-life-skills' resolves.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_ADULTING_LIFE_SKILLS_MODULE: LearningModule = {
	id: 'school-adulting-life-skills',
	title: 'Adulting Life Skills',
	description:
		'The worldview that treats boring competence as a financial superpower: see your bills, read your contracts, and keep enough order that normal problems never become emergencies.',
	emoji: '🧰',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'money-snapshot',
	order: 1,
	surface: 'school',
	homeSchool: 'adulting-life-skills',
	lessons: [
		// ── Lesson 1 ─────────────────────────────────────────────────────
		{
			id: 'als-bills-are-promises',
			title: 'Bills are promises with dates',
			description:
				'This lesson teaches why adulting life skills treats bills as timing promises, not just expenses.',
			pillar: 'money-snapshot',
			schoolsOfThought: ['adulting-life-skills'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 4,
			difficulty: 1,
			tags: ['bills', 'due-dates', 'money-calendar'],
			prerequisites: [],
			relatedLessons: [
				'als-contracts-hide-claims',
				'als-competence-buys-calm',
			],
			lessonKind: 'school-core',
			concepts: [
				'bill visibility',
				'due date awareness',
				'money calendar',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Adult life gets stressful when normal bills surprise you. Adulting life skills notices that money trouble often starts when obligations are invisible.',
				},
				{
					type: 'paragraph',
					text: 'This lens treats every recurring bill as a promise with a date. Rent, insurance, phone bills, utilities, subscriptions, and loan payments all claim future money before you get to make new choices.',
				},
				{
					type: 'bullet_list',
					items: [
						'What is due',
						'When it is due',
						'What happens if it is missed',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging your money by today’s balance while ignoring tomorrow’s claims.',
				},
				{
					type: 'example',
					text: 'A person may feel fine with $900 in checking, then rent, car insurance, and utilities land in the same week. The problem was not only the bills. The problem was not seeing the calendar.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can feel small and boring. But boring visibility prevents fees, panic, and avoidable stress.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to Dashboard because the first adulting move is seeing your current money picture clearly.',
				},
				{
					type: 'key_takeaway',
					text: 'A bill is not just a cost. It is a promise your future paycheck may already owe.',
				},
			],
			audioScript:
				'Adulting life skills has a simple way of seeing money: the bills are not just expenses, they are promises with dates attached. Rent is a promise. Insurance is a promise. A phone bill is a promise. A loan payment is a promise. Even a small subscription is a promise if it keeps pulling money from your account every month.\n\nThe lens is not trying to make life boring. It is trying to make life stable. A lot of money stress does not come from one huge disaster. It comes from small commitments that are scattered, forgotten, or misunderstood. You think you have enough money because the balance looks fine today. Then the rent hits, the car insurance renews, the utility bill posts, and suddenly your brain is trying to solve five problems at once.\n\nThis worldview pays attention to timing. It asks: when does money come in, when does money leave, and which dates cannot be missed without a consequence? That sounds basic, but basic is where a lot of adult life either gets calmer or more chaotic. A person can be smart, ambitious, and hardworking, but if their bills are not visible, they are forced to rely on memory. Memory is a bad operating system for adulthood.\n\nPicture someone who earns two thousand eight hundred dollars a month after taxes. Their rent is one thousand four hundred. Car insurance is one hundred eighty. Phone is seventy. Utilities float between one hundred and two hundred. Groceries hit in pieces. Gas hits whenever the tank gets low. On paper, they might feel like they are fine because no single bill looks impossible. But if they do not know the calendar, the month can ambush them. Adulting life skills looks at that and says: the issue is not only income. The issue is visibility.\n\nThis is why a due date can matter as much as a dollar amount. A five hundred dollar bill due next week feels different from a five hundred dollar bill due in two months. A yearly fee feels cheap until it arrives during a tight week. A late fee is not only a fee. It is a signal that the system was depending too much on luck.\n\nThe tradeoff is that this lens can feel small. It is not flashy. Nobody brags about knowing their renewal dates. Nobody feels rich because their documents are organized. But these quiet systems buy calm. They reduce fees, panic, overdrafts, missed due dates, and the weird shame spiral that comes from being surprised by normal bills.\n\nInside Nisk, this lesson connects to your Dashboard because Dashboard is where your money picture becomes visible. Not perfect. Not judged. Just visible. Adulting life skills does not ask you to become a finance expert first. It asks you to stop letting normal adult obligations hide in the fog. Once you can see the promises you already made, you can think clearly about the next one.\n\nThe grown up move is not perfection. It is catching the obvious stuff before it turns into pressure early. A calm month usually starts as a visible month.',
			flashcards: [
				{
					id: 'als-bills-are-promises-fc-main',
					role: 'main_idea',
					front: 'What is a bill?',
					back: 'A promise that future money is already expected to cover.',
				},
				{
					id: 'als-bills-are-promises-fc-watch',
					role: 'watch_out',
					front: 'What is the trap?',
					back: 'Judging your money by today’s balance while ignoring due dates.',
				},
				{
					id: 'als-bills-are-promises-fc-use',
					role: 'use_it',
					front: 'Where do you test this?',
					back: 'Use Dashboard to make your current money picture visible.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your money picture',
			},
			revision: 1,
		},

		// ── Lesson 2 ─────────────────────────────────────────────────────
		{
			id: 'als-contracts-hide-claims',
			title: 'Contracts hide future claims',
			description:
				'This lesson teaches why adulting life skills reads contracts as future claims on money, time, and options.',
			pillar: 'money-snapshot',
			schoolsOfThought: ['adulting-life-skills'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['contracts', 'commitment-cost', 'future-claims'],
			prerequisites: ['als-bills-are-promises'],
			relatedLessons: [
				'als-bills-are-promises',
				'als-competence-buys-calm',
			],
			lessonKind: 'school-core',
			concepts: ['contract risk', 'commitment cost', 'future claims'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A contract can feel harmless when the monthly number looks manageable. Adulting life skills notices the part people miss: the rules around the number.',
				},
				{
					type: 'paragraph',
					text: 'This lens sees leases, car payments, phone plans, subscriptions, insurance policies, and payment plans as future claims. The cost is not only what you pay today. It is what you are locked into later.',
				},
				{
					type: 'bullet_list',
					items: ['Price', 'Terms', 'Exit cost'],
				},
				{
					type: 'callout',
					text: 'The common mistake is asking “Can I afford the first payment?” instead of “What am I agreeing to over time?”',
				},
				{
					type: 'example',
					text: 'A rent amount may look doable, but parking, utilities, deposits, renewal rules, and lease break fees can change the real commitment.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that this lens can make you overly cautious. Some commitments are normal and useful. The point is not fear. The point is clear agreement.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to Simulate because recurring obligations can be tested against your real numbers before they become automatic.',
				},
				{
					type: 'key_takeaway',
					text: 'A contract is not just paperwork. It is a future claim on your options.',
				},
			],
			audioScript:
				'Adulting life skills looks at contracts with suspicion, not because every contract is bad, but because every contract can outlive the mood you were in when you signed it. A rental lease, a car loan, a phone plan, a gym agreement, a storage unit, a subscription, an insurance policy, even a buy now pay later plan, they all have rules. The problem is that people often focus on the monthly number and ignore the rules around the number.\n\nThis school is paying attention to quiet risk. The risk is not always that you cannot afford the first payment. The risk is that you do not know what happens if life changes. What happens if you move? What happens if your hours get cut? What happens if the car needs repairs? What happens if the promotional price ends? What happens if canceling takes thirty days and two phone calls? Adulting life skills thinks those questions are part of the cost.\n\nThere is a difference between a price and a commitment. A price is what something costs today. A commitment is what it can demand from you later. A two hundred dollar monthly payment is not only two hundred dollars. It is two hundred dollars repeated, protected by terms, fees, penalties, and deadlines. This lens does not say every commitment is wrong. It says commitments deserve respect because they can shrink your options.\n\nImagine someone signs an apartment lease because the rent fits the first month. Then they discover utilities are higher than expected, parking costs extra, internet is not included, the deposit tied up their cash, and breaking the lease would cost two months of rent. The lesson is not that renting is bad. The lesson is that the contract carried more reality than the headline price showed.\n\nThis is also where adulting life skills can feel annoying. It slows you down. It asks you to read boring details, ask plain questions, and imagine future inconvenience before you are excited. That can feel defensive. The blind spot is that too much caution can turn every choice into fear. You still need to live, move, sign things, and take normal adult steps. The point is not to avoid all commitments. The point is to know what you are agreeing to.\n\nInside Nisk, this lesson connects to simulation because some commitments deserve a future view before they become automatic. You can test what a recurring obligation does to your monthly room, your runway, or your freedom plan. The adulting lens is simple: before a contract gets your signature, it deserves your attention. A good contract question is not fancy. It can be as plain as: what am I required to pay, when does the price change, what happens if I am late, what happens if I need out, and what is not included? Those questions do not make you paranoid. They make you less dependent on hope.\n\nThink about a car decision. The payment may fit, but the full commitment may include insurance, gas, maintenance, registration, parking, repairs, and the risk of being stuck if your income changes. Adulting life skills does not tell you which car to choose. It tells you to see the whole shape of the obligation before the excitement edits the story.\n\nThat is the useful part of this worldview. It respects boring details because boring details can become expensive. It treats contracts like future claims on your time, attention, and money. When you can name those claims early, you keep more room to breathe.',
			flashcards: [
				{
					id: 'als-contracts-hide-claims-fc-main',
					role: 'main_idea',
					front: 'What does a contract create?',
					back: 'A future claim on money, time, attention, and options.',
				},
				{
					id: 'als-contracts-hide-claims-fc-watch',
					role: 'watch_out',
					front: 'What question is too small?',
					back: '“Can I afford the first payment?” misses the full commitment.',
				},
				{
					id: 'als-contracts-hide-claims-fc-use',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Test recurring obligations against your real numbers before they become automatic.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test a recurring obligation',
			},
			revision: 1,
		},

		// ── Lesson 3 ─────────────────────────────────────────────────────
		{
			id: 'als-competence-buys-calm',
			title: 'Competence buys calm',
			description:
				'This lesson teaches why adulting life skills treats basic organization as a protection against avoidable chaos.',
			pillar: 'money-snapshot',
			schoolsOfThought: ['adulting-life-skills'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 1,
			tags: ['organization', 'document-readiness', 'preparation'],
			prerequisites: ['als-bills-are-promises'],
			relatedLessons: [
				'als-bills-are-promises',
				'als-contracts-hide-claims',
			],
			lessonKind: 'school-core',
			concepts: [
				'basic competence',
				'document readiness',
				'fewer scrambles',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Some money stress is not caused by being broke. It is caused by being unprepared when normal life asks for paperwork, proof, payment, or a decision.',
				},
				{
					type: 'paragraph',
					text: 'Adulting life skills believes competence buys calm. Knowing where your documents are, what bills exist, what coverage you have, and who to contact can turn chaos into a normal task.',
				},
				{
					type: 'bullet_list',
					items: [
						'Documents in one place',
						'Due dates visible',
						'Important terms understood',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking basic organization does not matter until something goes wrong.',
				},
				{
					type: 'example',
					text: 'A car registration problem feels huge if insurance proof, account login, and payment timing are scattered. The same problem feels manageable when the system is ready.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can underweight ambition. Checking boxes is not the whole dream. It is the floor that gives bigger goals a safer place to stand.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lesson works best as reflection. Some adulting work is not math. It is judgment, preparation, and reducing fragile spots.',
				},
				{
					type: 'key_takeaway',
					text: 'Basic competence is not glamorous, but it gives your future self fewer fires to put out.',
				},
			],
			audioScript:
				'Adulting life skills has a blunt belief: competence buys calm. Not status. Not perfection. Calm. The ability to find the document, know who to call, understand the bill, protect the password, replace the tire, ask the landlord the right question, and keep enough order that normal problems do not become full emergencies.\n\nThis school notices the hidden cost of being disorganized. Losing a document costs time. Missing an insurance detail costs stress. Forgetting a renewal costs money. Not knowing where your title, lease, tax form, medical card, or account login lives can turn a normal task into a whole day of frustration. None of this looks like finance content at first. But adult life is full of small systems, and those systems affect your money.\n\nThe mistake is thinking adulthood is one big dramatic leap. It is usually a set of small abilities repeated over time. You learn how bills arrive. You learn which documents matter. You learn what insurance is supposed to protect. You learn how to keep records. You learn how to ask for terms in writing. You learn that a clean process can save you from panic later.\n\nImagine your car registration is due, your insurance card is missing, and your budget is already tight. If everything is scattered, the problem feels bigger than it is. If your documents are in one place, your due dates are visible, and your Dashboard shows your current cash position, the same problem becomes manageable. The situation did not change. Your operating system changed.\n\nThe tradeoff is that this lens can underweight ambition. A person can get so focused on basic competence that they delay bigger moves forever. Adulting life skills can become a comfort zone if it turns into checking boxes without building toward anything larger. The stronger version is different: handle the basics so you have more energy for the bigger game. Stability is not the whole dream. It is the floor under the dream.\n\nInside Nisk, this lesson is best used as a reflection. There may not be a perfect button for every adulting task, because some of the work is not math. Some of it is judgment, organization, and preparation. The lens asks: where is your life quietly fragile because nobody taught you the boring system? Fixing that is not glamorous. But it can give your future self fewer fires to put out. A useful way to think about this is the difference between a problem and a scramble. A problem is real. A scramble is the extra chaos created because the basics were not findable, visible, or prepared. Adulting life skills cannot remove every problem. It can remove a lot of scrambles.\n\nFor example, a medical bill can be stressful either way. But it is less confusing when you know where your insurance card is, what account paid the bill, what date it arrived, and what question to ask before paying. A landlord issue can still be annoying. But it is less messy when you saved the lease, took pictures, and kept important messages in writing.\n\nThis worldview is not about becoming a perfect adult. Perfect adults do not exist. It is about becoming harder to knock off balance. When the boring systems are handled, your money decisions get cleaner, your stress has fewer hiding places, and your future self inherits fewer messes from your current self.',
			flashcards: [
				{
					id: 'als-competence-buys-calm-fc-main',
					role: 'main_idea',
					front: 'What does competence buy?',
					back: 'Calm, clarity, and fewer avoidable scrambles.',
				},
				{
					id: 'als-competence-buys-calm-fc-watch',
					role: 'watch_out',
					front: 'What is the trap?',
					back: 'Ignoring basic organization until a normal problem becomes urgent.',
				},
				{
					id: 'als-competence-buys-calm-fc-use',
					role: 'use_it',
					front: 'What should you inspect?',
					back: 'Find one fragile adult system before it becomes a fire.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find one fragile system',
				prompt: 'List one bill, document, contract, or recurring task that would become stressful if you had to handle it tomorrow.',
			},
			revision: 1,
		},
	],
};
